import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export interface PreviewCreative {
  title: string;
  mediaLabel?: string;
  imageUrl: string | null;
  // True when imageUrl is only Meta's 64px thumbnail. Upscaling that to fill the
  // panel just renders a blurry mess, so it's shown at a size it can actually
  // hold instead, with a pointer to the live creative.
  imageIsThumbnail?: boolean;
  headline: string | null;
  primaryText: string | null;
  ctaType: string | null;
  links: { label: string; href: string }[];
  meta?: { label: string; value: string }[];
}

interface Props {
  creative: PreviewCreative | null;
  onClose: () => void;
}

// Empty state, shared by "nothing stored" and "stored but unreachable" so a
// failed load never leaves the panel showing a broken-image icon — which is what
// it did before, because the <img> had no onError at all.
function PreviewPlaceholder({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        height: 200,
        borderRadius: 1,
        border: '1px dashed #E2E8F0',
        backgroundColor: '#F8FAFC',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        textAlign: 'center',
      }}
    >
      <Typography variant="caption" sx={{ color: '#64748B' }}>
        {children}
      </Typography>
    </Box>
  );
}

// The image, with the two states it always had but never showed. It is fetched
// over the network on open, so "nothing there yet" and "nothing there" are
// different things and the panel has to say which — an unannounced blank is
// exactly what made this look broken.
function PreviewImage({ creative }: { creative: PreviewCreative }) {
  const [state, setState] = React.useState<'loading' | 'ready' | 'failed'>('loading');

  // A new creative is a new request; without this the second ad opened would
  // inherit the first one's 'ready' and never show its own loading state.
  React.useEffect(() => setState('loading'), [creative.imageUrl]);

  if (!creative.imageUrl) {
    return <PreviewPlaceholder>No preview image stored</PreviewPlaceholder>;
  }

  if (state === 'failed') {
    return (
      <PreviewPlaceholder>
        Meta no longer has this creative image — open the post to see the ad as it ran
      </PreviewPlaceholder>
    );
  }

  return (
    <>
      {state === 'loading' && <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />}
      <Box
        component="img"
        src={creative.imageUrl}
        alt=""
        onLoad={() => setState('ready')}
        onError={() => setState('failed')}
        sx={{
          width: creative.imageIsThumbnail ? 'auto' : '100%',
          maxWidth: '100%',
          maxHeight: creative.imageIsThumbnail ? 128 : undefined,
          display: state === 'loading' ? 'none' : 'block',
          borderRadius: 1,
          border: '1px solid #E2E8F0',
          backgroundColor: '#F8FAFC',
        }}
      />
      {state === 'ready' && creative.imageIsThumbnail && (
        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.75 }}>
          Meta only stores a thumbnail for this creative — open the post to see it
          full size.
        </Typography>
      )}
    </>
  );
}

// The creative as a human would judge it: the image at a size you can actually
// read, the copy in full rather than line-clamped, and every way out to see it
// live on Facebook.
export default function CreativePreview({ creative, onClose }: Props) {
  return (
    <Dialog open={!!creative} onClose={onClose} maxWidth="md" fullWidth>
      {creative && (
        <>
          <DialogTitle sx={{ pr: 6, fontSize: 18 }}>
            {creative.title}
            {creative.mediaLabel && (
              <Chip
                label={creative.mediaLabel}
                size="small"
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: 11,
                  color: '#4F46E5',
                  backgroundColor: 'rgba(99, 102, 241, 0.10)',
                }}
              />
            )}
            <IconButton
              onClick={onClose}
              sx={{ position: 'absolute', right: 8, top: 8 }}
              aria-label="Close preview"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
                gap: 3,
              }}
            >
              <Box>
                <PreviewImage creative={creative} />

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                  {creative.links.map(link => (
                    <Button
                      key={link.label}
                      size="small"
                      variant="outlined"
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.label}
                    </Button>
                  ))}
                </Box>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                {creative.headline && (
                  <>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      Headline
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {creative.headline}
                    </Typography>
                  </>
                )}

                {creative.primaryText && (
                  <>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      Primary text
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', mb: 2, lineHeight: 1.5 }}
                    >
                      {creative.primaryText}
                    </Typography>
                  </>
                )}

                {creative.ctaType && (
                  <>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      Call to action
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {creative.ctaType.replace(/_/g, ' ').toLowerCase()}
                    </Typography>
                  </>
                )}

                {creative.meta && creative.meta.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                        gap: 1.5,
                      }}
                    >
                      {creative.meta.map(item => (
                        <Box key={item.label}>
                          <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                            {item.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}
                          >
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}
